import { tool } from 'ai';
import { z } from 'zod';
import { InboxItemApplicationService } from '../../../inbox-management/application/services/InboxItemApplicationService';
import { CreateInboxItemCommand } from '../../../inbox-management/application/commands/CreateInboxItemCommand';

/**
 * GTD Inbox Tools - Co-located with Domain Logic
 * 
 * Moved from packages/ai-domain/src/bounded-contexts/vercel-core-ai/tools/inbox-tools.ts
 * Following Eric Evans DDD principles: tools live within the domain that contains the knowledge
 */

export function createInboxTools(inboxService: InboxItemApplicationService) {
  const listInboxItems = tool({
    description: 'List items in the GTD inbox with filtering options',
    parameters: z.object({
      status: z.enum(['unprocessed', 'processed', 'all']).default('all').describe('Filter by processing status'),
      limit: z.number().default(10).describe('Maximum number of items to return'),
    }),
    execute: async ({ status, limit }) => {
      try {
        // Direct access to domain service - no integration port needed!
        const result = await inboxService.listInboxItems({
          status: status === 'all' ? undefined : status,
          limit: Math.min(limit, 50)
        });
        
        return {
          success: true,
          items: result.items.map(item => ({
            id: item.id,
            content: item.originalContent,
            status: item.processingStatus,
            capturedAt: item.capturedAt,
            capturedBy: item.capturedByPersonId,
          })),
          totalCount: result.totalCount,
          message: `Found ${result.items.length} inbox items`
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          message: 'Failed to retrieve inbox items'
        };
      }
    },
  });

  const addInboxItem = tool({
    description: 'Add a new item to the GTD inbox',
    parameters: z.object({
      content: z.string().describe('The content/description of the item to add'),
      personId: z.string().default('system').describe('ID of the person adding the item'),
      priority: z.enum(['low', 'medium', 'high', 'urgent']).optional().describe('Priority level'),
    }),
    execute: async ({ content, personId }) => {
      try {
        const command: CreateInboxItemCommand = {
          originalContent: content,
          capturedByPersonId: personId
        };
        
        const result = await inboxService.createInboxItem(command);
        
        return {
          success: true,
          itemId: result.itemId,
          message: `Added "${content}" to inbox`,
          capturedAt: result.capturedAt
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          message: 'Failed to add item to inbox'
        };
      }
    },
  });

  const searchInboxItems = tool({
    description: 'Search for items in the GTD inbox by content',
    parameters: z.object({
      query: z.string().describe('Search term to find in inbox item content'),
      limit: z.number().default(5).describe('Maximum number of results'),
    }),
    execute: async ({ query, limit }) => {
      try {
        // Simple search implementation using list and filter
        const allItems = await inboxService.listInboxItems({ limit: 100 });
        
        const filteredItems = allItems.items.filter(item => 
          item.originalContent.toLowerCase().includes(query.toLowerCase()) ||
          (item.clarification && item.clarification.toLowerCase().includes(query.toLowerCase()))
        ).slice(0, Math.min(limit, 20));
        
        return {
          success: true,
          items: filteredItems.map(item => ({
            id: item.id,
            content: item.originalContent,
            status: item.processingStatus,
            relevance: 'high', // Could implement relevance scoring
          })),
          query,
          resultCount: filteredItems.length,
          message: `Found ${filteredItems.length} items matching "${query}"`
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          message: `Failed to search inbox for "${query}"`
        };
      }
    },
  });

  return {
    listInboxItems,
    addInboxItem,
    searchInboxItems,
  };
}